import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { formatMonthlyPrice } from '../../utils/pricing';

const STATUS_LABELS = {
  pending: 'Pending approval',
  approved: 'Approved',
  declined: 'Declined',
  cancelled: 'Cancelled',
};

const STATUS_TONE = {
  pending: 'pending',
  approved: 'success',
  declined: 'danger',
  cancelled: 'neutral',
};

const toNumericAmount = (value) => {
  if (value == null) {
    return null;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  const numeric = Number(String(value).replace(/[^0-9.]/g, ''));
  return Number.isFinite(numeric) ? numeric : null;
};

const resolveMonthlyPriceLabel = (source = {}) => {
  const amountCandidates = [
    source.priceAmount,
    source.planPriceAmount,
    source.price,
    source.planPriceDisplay,
  ];
  const resolvedAmount =
    amountCandidates.map(toNumericAmount).find((candidate) => candidate != null) || 0;

  const periodCandidates = [source.billingPeriod, source.planBillingPeriod];
  const resolvedPeriod =
    periodCandidates.find((candidate) => candidate != null) || 1;

  const unit =
    source.billingUnit ||
    source.planBillingUnit ||
    source.periodUnit ||
    'month';

  return formatMonthlyPrice(resolvedAmount, resolvedPeriod, unit);
};

const SubscriptionPanel = ({
  subscription,
  queuedSubscriptions,
  plans,
  preferredPlanId,
  pendingRequest,
  requestHistory,
  onPlanSubmit,
  onCancelRequest,
  isSubmitting,
  isCancelling,
  isLoading,
}) => {
  const subscriptionPlanId = subscription && subscription.planId ? subscription.planId : null;
  const quotaLimit = subscription && Number.isFinite(subscription.quotaLimit) ? subscription.quotaLimit : null;
  const quotaUsed = subscription ? Number(subscription.quotaUsed || 0) : 0;
  const quotaRemaining =
    subscription && subscription.quotaRemaining != null ? Number(subscription.quotaRemaining) : quotaLimit != null ? Math.max(quotaLimit - quotaUsed, 0) : null;
  const quotaPercent =
    quotaLimit != null && quotaLimit > 0 ? Math.min(100, Math.round((quotaUsed / quotaLimit) * 100)) : null;
  const isQuotaExhausted = Boolean(subscription && subscription.isQuotaExhausted);
  const [selectedPlan, setSelectedPlan] = useState(() => {
    if (pendingRequest) {
      return pendingRequest.planId;
    }
    if (subscriptionPlanId) {
      return subscriptionPlanId;
    }
    if (preferredPlanId && plans.some((plan) => plan.id === preferredPlanId)) {
      return preferredPlanId;
    }
    return plans[0] ? plans[0].id : '';
  });

  useEffect(() => {
    const availablePlanIds = plans.map((plan) => plan.id);
    if (pendingRequest && availablePlanIds.includes(pendingRequest.planId)) {
      setSelectedPlan(pendingRequest.planId);
      return;
    }
    if (subscriptionPlanId && availablePlanIds.includes(subscriptionPlanId)) {
      setSelectedPlan(subscriptionPlanId);
      return;
    }
    if (preferredPlanId && availablePlanIds.includes(preferredPlanId)) {
      setSelectedPlan(preferredPlanId);
      return;
    }
    if (plans.length > 0) {
      setSelectedPlan(plans[0].id);
    }
  }, [pendingRequest, preferredPlanId, subscriptionPlanId, plans]);

  const activePlan = useMemo(() => {
    if (!subscriptionPlanId && !(subscription && subscription.planName)) {
      return null;
    }

    const fromPlans = subscriptionPlanId
      ? plans.find((plan) => plan.id === subscriptionPlanId)
      : null;
    if (fromPlans) {
      return fromPlans;
    }
    if (subscription && subscription.planName) {
      const priceFallback = resolveMonthlyPriceLabel({
        planPriceAmount: subscription.planPriceAmount,
        planPriceDisplay: subscription.planPriceDisplay,
        planBillingPeriod: subscription.planBillingPeriod,
        planBillingUnit: subscription.planBillingUnit,
      });
      return {
        id: subscription.planId,
        name: subscription.planName,
        price: priceFallback,
        features: Array.isArray(subscription.planFeatures)
          ? subscription.planFeatures
          : [],
      };
    }
    return null;
  }, [plans, subscription, subscriptionPlanId]);

  const pendingPlanDetails = useMemo(() => {
    if (!pendingRequest) {
      return null;
    }
    const fromPlans = plans.find((plan) => plan.id === pendingRequest.planId);
    const priceLabel = fromPlans
      ? resolveMonthlyPriceLabel(fromPlans)
      : resolveMonthlyPriceLabel({
          planPriceAmount: pendingRequest.planPriceAmount,
          planPriceDisplay: pendingRequest.planPriceDisplay,
          planBillingPeriod: pendingRequest.planBillingPeriod,
          planBillingUnit: pendingRequest.planBillingUnit,
        });
    return {
      name: pendingRequest.planName || (fromPlans && fromPlans.name) || pendingRequest.planId,
      price: priceLabel,
    };
  }, [pendingRequest, plans]);

  const sortedPlans = useMemo(() => {
    if (!Array.isArray(plans)) {
      return [];
    }

    const intervalWeight = (plan) => {
      if (!plan) {
        return Number.POSITIVE_INFINITY;
      }
      const rawPeriod = plan.billingPeriod != null ? plan.billingPeriod : plan.period;
      const period = Number(rawPeriod);
      const unit = String(plan.billingUnit || plan.periodUnit || '').toLowerCase();
      if (!Number.isFinite(period) || period <= 0) {
        return Number.POSITIVE_INFINITY;
      }
      if (unit.startsWith('year')) {
        return period * 12;
      }
      if (unit.startsWith('month')) {
        return period;
      }
      if (unit.startsWith('day')) {
        return period / 30;
      }
      return Number.POSITIVE_INFINITY;
    };

    return [...plans].sort((a, b) => {
      const weightA = intervalWeight(a);
      const weightB = intervalWeight(b);
      if (weightA !== weightB) {
        return weightA - weightB;
      }
      const nameA = String((a && a.name) || '');
      const nameB = String((b && b.name) || '');
      return nameA.localeCompare(nameB, undefined, { sensitivity: 'base' });
    });
  }, [plans]);

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!selectedPlan) {
      return;
    }
    onPlanSubmit(selectedPlan);
  };

  const hasPendingRequest = Boolean(pendingRequest);
  const activePlanPrice =
    subscription && Number.isFinite(Number(subscription.planPriceAmount))
      ? Number(subscription.planPriceAmount)
      : null;
  const isDowngradePlan = (plan) => (
    activePlanPrice != null
      && Number.isFinite(Number(plan.priceAmount))
      && Number(plan.priceAmount) < activePlanPrice
  );
  const selectedPlanDetails = sortedPlans.find((plan) => plan.id === selectedPlan) || null;
  const selectedPlanIsDowngrade = selectedPlanDetails ? isDowngradePlan(selectedPlanDetails) : false;
  const disableSubmit = isLoading || isSubmitting || !selectedPlan || selectedPlanIsDowngrade;
  const submitLabel = hasPendingRequest
    ? 'Update request'
    : subscriptionPlanId && selectedPlan === subscriptionPlanId
    ? 'Queue another plan'
    : 'Submit request';
  const currentPlanLabel = isLoading
    ? 'Loading…'
    : (subscription && subscription.planName)
        || (activePlan && activePlan.name)
        || 'No active plan assigned';

  return (
    <section className="account-section account-section--subscription">
      <header className="account-section__header">
        <div>
          <h2>Credit Plan</h2>
          <p>Choose the plan you need and send it for admin approval.</p>
        </div>
      </header>

      <div className="subscription-overview">
        <div className="current-plan-card">
          <span className="current-plan-card__label">Current plan</span>
          <span className="current-plan-card__value">{currentPlanLabel}</span>
          {subscription && subscription.approvedAt && (
            <span className="current-plan-card__meta">
              Active since {dayjs(subscription.approvedAt).format('MMM D, YYYY')}
            </span>
          )}
          {!isLoading && !subscription && (
            <span className="current-plan-card__meta">
              No active credit plan assigned yet.
            </span>
          )}
        </div>
        {subscription && quotaLimit != null && (
          <div className={`subscription-quota${isQuotaExhausted ? ' subscription-quota--danger' : ''}`}>
            <div className="subscription-quota__header">
              <span>Credit limit</span>
              <strong>
                {quotaUsed.toLocaleString()} / {quotaLimit.toLocaleString()}
              </strong>
            </div>
            <div className="subscription-quota__meter" aria-hidden="true">
              <span style={{ width: `${quotaPercent || 0}%` }} />
            </div>
            <p className="subscription-quota__meta">
              {isQuotaExhausted
                ? 'You have used all available credits. Renew your plan to continue.'
                : `${(quotaRemaining || 0).toLocaleString()} credits remaining.`}
            </p>
          </div>
        )}
      </div>

      {hasPendingRequest && (
        <div className={`subscription-request subscription-request--${STATUS_TONE[pendingRequest.status] || 'pending'}`}>
          <h3>Pending Approval</h3>
          <p className="subscription-request__message">
            {pendingRequest.userName ? `${pendingRequest.userName} ` : ''}requested the{' '}
            <strong>{pendingPlanDetails ? pendingPlanDetails.name : pendingRequest.planId}</strong> plan on{' '}
            {dayjs(pendingRequest.submittedAt).format('MMM D, YYYY h:mm A')}.
          </p>
          <div className="form-actions form-actions--row">
            <button
              type="button"
              className="ghost-btn ghost-btn--danger"
              onClick={() => onCancelRequest(pendingRequest.id)}
              disabled={isCancelling}
            >
              {isCancelling ? 'Cancelling…' : 'Cancel request'}
            </button>
          </div>
        </div>
      )}

      {queuedSubscriptions.length > 0 && (
        <div className="subscription-history">
          <h3>Queued Plans</h3>
          <div className="subscription-history__table">
            <table>
              <thead>
                <tr>
                  <th scope="col">Plan</th>
                  <th scope="col">Price</th>
                  <th scope="col">Queue position</th>
                  <th scope="col">Purchased</th>
                </tr>
              </thead>
              <tbody>
                {queuedSubscriptions.map((entry, index) => (
                  <tr key={`${entry.planId || 'queued'}-${entry.createdAt || index}`}>
                    <td>{entry.planName || entry.planId}</td>
                    <td>{entry.planPriceDisplay || '—'}</td>
                    <td>{entry.queuePosition || index + 1}</td>
                    <td>
                      {entry.createdAt
                        ? dayjs(entry.createdAt).format('MMM D, YYYY h:mm A')
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {plans.length === 0 ? (
        <p className="subscription-overview__empty">
          No credit plans are currently available. Please check back later.
        </p>
      ) : (
        <form className="plan-selector" onSubmit={handleSubmit}>
          <h3>Select a Credit Plan</h3>
          <div className="plan-options">
            {sortedPlans.map((plan) => {
              const priceLabel = resolveMonthlyPriceLabel(plan);
              const downgradePlan = isDowngradePlan(plan);
              return (
                <label key={plan.id} className={`plan-card ${selectedPlan === plan.id ? 'plan-card--active' : ''}`}>
                  <input
                    type="radio"
                    name="plan"
                    value={plan.id}
                    checked={selectedPlan === plan.id}
                    onChange={() => setSelectedPlan(plan.id)}
                    disabled={isLoading || downgradePlan}
                  />
                  <div className="plan-card__header">
                    <span className="plan-card__name">{plan.name}</span>
                    <span className="plan-card__price">{priceLabel}</span>
                  </div>
                  <ul className="plan-card__features">
                    {(plan.features || []).map((feature) => (
                      <li key={feature}>{feature}</li>
                    ))}
                  </ul>
                  {downgradePlan && (
                    <p className="subscription-request__message">
                      Downgrades are not available while your current plan is active.
                    </p>
                  )}
                </label>
              );
            })}
          </div>
          <div className="form-actions">
            <button type="submit" className="primary-btn" disabled={disableSubmit}>
              {isSubmitting ? 'Sending…' : submitLabel}
            </button>
          </div>
        </form>
      )}

      {requestHistory.length > 0 && (
        <div className="subscription-history">
          <h3>Recent Requests</h3>
          <div className="subscription-history__table">
            <table>
              <thead>
                <tr>
                  <th scope="col">Plan</th>
                  <th scope="col">Status</th>
                  <th scope="col">Submitted</th>
                  <th scope="col">Resolved</th>
                </tr>
              </thead>
              <tbody>
                {requestHistory.map((entry) => (
                  <tr key={entry.id}>
                    <td>{entry.planName || entry.planId}</td>
                    <td>
                      <span className={`subscription-status subscription-status--${STATUS_TONE[entry.status] || 'neutral'}`}>
                        {STATUS_LABELS[entry.status] || entry.status}
                      </span>
                    </td>
                    <td>{dayjs(entry.submittedAt).format('MMM D, YYYY h:mm A')}</td>
                    <td>
                      {entry.resolvedAt
                        ? dayjs(entry.resolvedAt).format('MMM D, YYYY h:mm A')
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
};

SubscriptionPanel.propTypes = {
  subscription: PropTypes.shape({
    planId: PropTypes.string,
    planName: PropTypes.string,
    approvedAt: PropTypes.string,
    planPriceDisplay: PropTypes.string,
    planPriceAmount: PropTypes.number,
    planBillingPeriod: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    planBillingUnit: PropTypes.string,
    planFeatures: PropTypes.arrayOf(PropTypes.string),
    quotaLimit: PropTypes.number,
    quotaUsed: PropTypes.number,
    quotaRemaining: PropTypes.number,
    isQuotaExhausted: PropTypes.bool,
  }),
  queuedSubscriptions: PropTypes.arrayOf(
    PropTypes.shape({
      planId: PropTypes.string,
      planName: PropTypes.string,
      planPriceDisplay: PropTypes.string,
      createdAt: PropTypes.string,
      queuePosition: PropTypes.number,
    }),
  ),
  plans: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      price: PropTypes.string,
      priceAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      billingPeriod: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      billingUnit: PropTypes.string,
      period: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      periodUnit: PropTypes.string,
      features: PropTypes.arrayOf(PropTypes.string).isRequired,
    }),
  ).isRequired,
  preferredPlanId: PropTypes.string,
  pendingRequest: PropTypes.shape({
    id: PropTypes.string.isRequired,
    userId: PropTypes.string.isRequired,
    userName: PropTypes.string,
    planId: PropTypes.string.isRequired,
    planName: PropTypes.string,
    planPriceDisplay: PropTypes.string,
    planFeatures: PropTypes.arrayOf(PropTypes.string),
    planPriceAmount: PropTypes.number,
    planBillingPeriod: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    planBillingUnit: PropTypes.string,
    status: PropTypes.string.isRequired,
    submittedAt: PropTypes.string.isRequired,
    resolvedAt: PropTypes.string,
  }),
  requestHistory: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      userId: PropTypes.string.isRequired,
      planId: PropTypes.string.isRequired,
      planName: PropTypes.string,
      planPriceDisplay: PropTypes.string,
      status: PropTypes.string.isRequired,
      submittedAt: PropTypes.string.isRequired,
      resolvedAt: PropTypes.string,
    }),
  ).isRequired,
  onPlanSubmit: PropTypes.func.isRequired,
  onCancelRequest: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
  isCancelling: PropTypes.bool,
  isLoading: PropTypes.bool,
};

SubscriptionPanel.defaultProps = {
  subscription: null,
  queuedSubscriptions: [],
  preferredPlanId: null,
  pendingRequest: null,
  isSubmitting: false,
  isCancelling: false,
  isLoading: false,
};

export default SubscriptionPanel;
